import {
    ref, computed, watch, onMounted
} from 'vue';
import { useFeatureFlow } from '@agyletime/vue-3-common/src/composables/featureflow';
import { parseFacetsFromUrl } from '@/components/TeamAndSchedule/utils/facetUtils';
import type { FacetConfig, FacetItem } from '../types/facetsTypes';

interface FacetSection {
    id: string;
    title: string;
    facets: FacetConfig[];
}

export function useFacetVisibility(activeFacets: () => FacetConfig[], activeTable: () => string, viewSelected: () => boolean) {
    const featureFlow = useFeatureFlow();
    const isFacetVisibilityEnabled = computed(() => featureFlow('alph-3909-facet-visibility').isOn);
    const visibleFacets = ref<Record<string, Set<string>>>({});
    const facetHasValues = ref<Record<string, boolean>>({});
    const facetsFromUrl = parseFacetsFromUrl();

    const getVisibleFacetsForTable = (table: string) => {
        if (!visibleFacets.value[table]) {
            visibleFacets.value[table] = new Set();
        }
        return visibleFacets.value[table];
    };

    onMounted(() => {
        if (isFacetVisibilityEnabled.value) {
            const table = activeTable();
            const tableVisibleFacets = getVisibleFacetsForTable(table);
            activeFacets().forEach((facet) => {
                const visibleByDefault = Boolean(facet.visible) && !viewSelected();
                const facetInUrl = Boolean(facetsFromUrl && facetsFromUrl[table][facet.id]);
                if (visibleByDefault || facetInUrl) tableVisibleFacets.add(facet.id);
            });
        }
    });

    watch(() => activeFacets(), (newFacets, oldFacets) => {
        if (isFacetVisibilityEnabled.value && newFacets) {
            const table = activeTable();
            const tableVisibleFacets = getVisibleFacetsForTable(table);
            const isInitialLoad = !oldFacets || oldFacets.length === 0;
            const isTableEmpty = tableVisibleFacets.size === 0;

            if (isInitialLoad || isTableEmpty) {
                newFacets.forEach((facet) => {
                    const visibleByDefault = Boolean(facet.visible) && !viewSelected();
                    const facetInUrl = Boolean(facetsFromUrl && facetsFromUrl[table][facet.id]);
                    if (visibleByDefault || facetInUrl) tableVisibleFacets.add(facet.id);
                });
            } else {
                const oldFacetIds = new Set(oldFacets.map((facet) => facet.id));
                newFacets.forEach((facet) => {
                    const visibleByDefault = Boolean(facet.visible) && !viewSelected();
                    const facetInUrl = Boolean(facetsFromUrl && facetsFromUrl[table][facet.id]);
                    if (!oldFacetIds.has(facet.id)) {
                        if (visibleByDefault || facetInUrl) tableVisibleFacets.add(facet.id);
                    }
                });
            }

            const currentFacetIds = new Set(newFacets.map((f) => f.id));
            tableVisibleFacets.forEach((facetId) => {
                if (!currentFacetIds.has(facetId)) {
                    tableVisibleFacets.delete(facetId);
                }
            });
        }
    }, { immediate: true });

    const isFacetVisible = (facetId: string) => {
        if (!isFacetVisibilityEnabled.value) return true;
        const table = activeTable();
        const tableVisibleFacets = getVisibleFacetsForTable(table);
        return tableVisibleFacets.has(facetId);
    };

    const isFacetDisabled = (facetId: string) => {
        return facetHasValues.value[facetId] || false;
    };

    const toggleFacetVisibility = (facetId: string, event: Event, onVisibilityChange?: () => void) => {
        if (!isFacetVisibilityEnabled.value) return;

        const target = event.target as HTMLInputElement;
        const isChecked = target.checked;
        const table = activeTable();
        const tableVisibleFacets = getVisibleFacetsForTable(table);

        if (isChecked) {
            tableVisibleFacets.add(facetId);
        } else {
            if (isFacetDisabled(facetId)) {
                target.checked = true;
                return;
            }
            tableVisibleFacets.delete(facetId);
        }

        if (onVisibilityChange) {
            onVisibilityChange();
        }
    };

    const visibleFacetsForSection = (section: FacetSection) => {
        if (!isFacetVisibilityEnabled.value) {
            return section.facets;
        }
        const table = activeTable();
        const tableVisibleFacets = getVisibleFacetsForTable(table);
        return section.facets.filter((facet: FacetConfig) => tableVisibleFacets.has(facet.id));
    };

    const trackFacetValues = (facetId: string, facetValue: FacetItem) => {
        facetHasValues.value[facetId] = facetValue?.value && Array.isArray(facetValue?.value) && facetValue?.value?.length > 0;
    };

    const getFacetVisibilityState = () => {
        if (!isFacetVisibilityEnabled.value) return null;
        const table = activeTable();
        const tableVisibleFacets = getVisibleFacetsForTable(table);
        return Array.from(tableVisibleFacets);
    };

    const setFacetVisibilityState = (visibilityState: string[] | null) => {
        if (!isFacetVisibilityEnabled.value || !visibilityState) return;
        const table = activeTable();
        visibleFacets.value[table] = new Set(visibilityState);
    };

    const resetVisibilityFacetValues = () => {
        facetHasValues.value = {};
    };

    return {
        isFacetVisibilityEnabled,
        isFacetVisible,
        isFacetDisabled,
        toggleFacetVisibility,
        visibleFacetsForSection,
        trackFacetValues,
        getFacetVisibilityState,
        setFacetVisibilityState,
        resetVisibilityFacetValues
    };
}